import React, { useState, useCallback, useRef } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { MessageSquareText, Pencil, Check, Variable, AlertTriangle } from "lucide-react";
import VariableDropdown from "../FlowBuilder/VariableDropdown";
import { getUnknownVariableNames } from "../../utils/flowVariableValidation";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

const BG = "#ECFEFF";
const ACCENT = "#0D9488";
const ACCENT_SOFT = "#CCFBF1";

const CollapsedView = ({ data, onEdit }) => (
  <div
    className="relative rounded-xl overflow-hidden"
    style={{ backgroundColor: BG, padding: "10px", minWidth: "260px", maxWidth: "300px" }}
  >
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onEdit();
      }}
      className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
      title="Edit node"
    >
      <Pencil size={12} style={{ color: ACCENT }} />
    </button>

    <div className="bg-white rounded-lg shadow-sm p-3">
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 rounded-lg" style={{ backgroundColor: ACCENT_SOFT }}>
          <MessageSquareText size={14} style={{ color: ACCENT }} />
        </div>
        <span className="text-[12px] font-bold text-teal-900">Ask question (text)</span>
      </div>

      {data.question ? (
        <div className="flex items-start gap-1.5 mb-2">
          <span className="text-[11px] text-gray-700 leading-tight">
            {data.question.length > 80 ? `${data.question.substring(0, 80)}…` : data.question}
          </span>
        </div>
      ) : (
        <p className="text-[11px] text-gray-400 italic mb-2">No question set</p>
      )}

      <div className="flex items-center gap-1.5">
        <Variable size={10} className="text-[#008069] flex-shrink-0" />
        <span className="text-[10px] font-mono font-semibold text-[#008069]">
          {`{{${data.variableName || "selected"}}}`}
        </span>
      </div>
      <p className="text-[9px] text-teal-600/80 mt-1.5">Stores raw reply — no AI</p>
    </div>
  </div>
);

const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState({
    question: data.question || "",
    variableName: data.variableName || "selected",
    emptyAnswerMessage: data.emptyAnswerMessage || "",
  });
  const questionRef = useRef(null);

  const unknownInQuestion = getUnknownVariableNames(form.question, flowVariables);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const insertVariableIntoQuestion = (varName) => {
    if (!varName) return;
    const current = form.question || "";
    const placeholder = `{{${varName}}}`;
    const textarea = questionRef.current;
    if (textarea) {
      const pos = textarea.selectionStart ?? current.length;
      const before = current.slice(0, pos);
      const after = current.slice(pos);
      updateField("question", before + placeholder + after);
    } else {
      updateField("question", current + placeholder);
    }
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: BG, borderRadius: "12px", padding: "14px", minWidth: "320px", maxWidth: "400px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg" style={{ backgroundColor: ACCENT_SOFT }}>
            <MessageSquareText size={14} style={{ color: ACCENT }} />
          </div>
          <div>
            <span className="text-[13px] font-bold text-teal-900 block">Ask question (text)</span>
            <span className="text-[9px] text-teal-700/80">Saves the user&apos;s message as-is</span>
          </div>
        </div>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() =>
              onSave({
                question: form.question,
                variableName: (form.variableName || "selected").trim() || "selected",
                emptyAnswerMessage: form.emptyAnswerMessage || "",
              })
            }
            className="px-3 py-1.5 text-[11px] font-bold text-white rounded-lg transition-colors flex items-center gap-1 shadow-sm"
            style={{ backgroundColor: ACCENT }}
          >
            <Check size={12} />
            Save
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="captureText">
      <div className="bg-white rounded-lg shadow-sm p-3 space-y-3 max-h-[420px] overflow-y-auto">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider">
              Question to send
            </label>
            <VariableDropdown
              value=""
              onChange={insertVariableIntoQuestion}
              flowVariables={flowVariables}
              placeholder="Insert variable"
              className="w-32"
            />
          </div>
          <textarea
            ref={questionRef}
            value={form.question}
            onChange={(e) => updateField("question", e.target.value)}
            placeholder="What would you like to know?"
            rows={3}
            className="w-full px-3 py-2 text-[12px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-teal-400 resize-none placeholder:text-gray-400"
          />
          {unknownInQuestion.length > 0 && (
            <div className="flex items-start gap-1 mt-1 px-2 py-1 bg-amber-50 rounded text-[9px] text-amber-700">
              <AlertTriangle size={10} className="flex-shrink-0 mt-0.5" />
              <span>
                Unknown variable(s): <strong>{unknownInQuestion.join(", ")}</strong>
              </span>
            </div>
          )}
        </div>

        <div>
          <label className="flex items-center gap-1 text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1">
            <Variable size={10} />
            Save reply in variable
          </label>
          <VariableDropdown
            value={form.variableName}
            onChange={(name) => updateField("variableName", name)}
            flowVariables={flowVariables}
            allowCreate
            onCreateVariable={onCreateVariable}
            placeholder="selected"
          />
        </div>

        <div>
          <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider mb-1 block">
            If reply is empty (optional)
          </label>
          <input
            type="text"
            value={form.emptyAnswerMessage}
            onChange={(e) => updateField("emptyAnswerMessage", e.target.value)}
            placeholder='Default: "Please send a text reply."'
            className="w-full px-3 py-2 text-[11px] text-gray-800 bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-teal-300 placeholder:text-gray-400"
          />
          <p className="text-[9px] text-gray-400 mt-1">Sent again when the user sends whitespace-only; flow waits until non-empty text.</p>
        </div>
      </div>

      </NodeEditorTabs>
      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={() =>
            onSave({
              question: form.question,
              variableName: (form.variableName || "selected").trim() || "selected",
              emptyAnswerMessage: form.emptyAnswerMessage || "",
            })
          }
          className="flex-1 py-2 text-[12px] font-bold text-white rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm"
          style={{ backgroundColor: ACCENT }}
        >
          <Check size={14} />
          Save
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const CaptureTextNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("captureText");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, {
          ...data,
          label: data.label || "Ask question (text)",
          ...formData,
        });
      }
      closeEditor();
    },
    [id, data]
  );

  const handleCancel = useCallback(() => closeEditor(), []);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !border-2 !border-white !shadow-sm"
        style={{ left: -7, backgroundColor: ACCENT }}
      />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={handleCancel}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !border-2 !border-white !shadow-sm"
        style={{ right: -7, backgroundColor: ACCENT }}
      />
    </div>
  );
};

export default CaptureTextNode;
